import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import {
    ArrowLeft, Loader2, RefreshCw, Play, Pause, XCircle, FileText,
    PhoneCall, Clock, CheckCircle2, AlertTriangle, Trash2, RotateCcw, Gauge
} from "lucide-react";
import { toast } from "sonner";
import {
    getCampaignById, fetchCampaignCalls, startCampaign, pauseCampaign, cancelCampaign,
    removeCampaignCalls, rerunCampaign, updateCampaignSettings
} from "../../services/crmServices/campaignService";
import { fetchLeadStatuses } from "../../services/crmServices/leadStatusService";
import { CallAnalysisPanel } from "./CallAnalysisPanel";

const CAMPAIGN_STATUS_STYLES = {
    draft: "bg-gray-100 text-gray-700",
    running: "bg-green-100 text-green-700",
    paused: "bg-yellow-100 text-yellow-700",
    completed: "bg-blue-100 text-blue-700",
    cancelled: "bg-red-100 text-red-700"
};

const DURATION_RANGES = {
    "": { label: "Any duration" },
    "0-29": { label: "Under 30s", min: 0, max: 29 },
    "30-59": { label: "30s – 1m", min: 30, max: 59 },
    "60-179": { label: "1 – 3m", min: 60, max: 179 },
    "180-299": { label: "3 – 5m", min: 180, max: 299 },
    "300-": { label: "Over 5m", min: 300 }
};

const CALL_STATUS_STYLES = {
    queued: "bg-gray-100 text-gray-600",
    calling: "bg-green-100 text-green-700",
    completed: "bg-blue-100 text-blue-700",
    failed: "bg-red-100 text-red-700",
    cancelled: "bg-gray-100 text-gray-400"
};

function formatDuration(seconds) {
    if (!seconds && seconds !== 0) return "—";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

export function CampaignDetail({ campaignId, onBack }) {
    const [campaign, setCampaign] = useState(null);
    const [calls, setCalls] = useState([]);
    const [totalCalls, setTotalCalls] = useState(0);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState("");
    const [durationFilter, setDurationFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [transcriptCall, setTranscriptCall] = useState(null);
    const [selected, setSelected] = useState(() => new Set());
    const [leadStatuses, setLeadStatuses] = useState([]);
    const [removeStatus, setRemoveStatus] = useState("");
    const [removing, setRemoving] = useState(false);
    const [speedInput, setSpeedInput] = useState("");
    const [savingSpeed, setSavingSpeed] = useState(false);

    const limit = 50;

    const canEdit = campaign && campaign.status !== "running";

    const load = useCallback(async () => {
        try {
            const range = DURATION_RANGES[durationFilter] || {};
            const [campaignRes, callsRes] = await Promise.all([
                getCampaignById(campaignId),
                fetchCampaignCalls(campaignId, {
                    page, limit,
                    status: statusFilter || undefined,
                    minDuration: range.min !== undefined ? range.min : undefined,
                    maxDuration: range.max !== undefined ? range.max : undefined
                })
            ]);
            if (campaignRes?.data) {
                setCampaign(campaignRes.data);
                setSpeedInput((prev) => (prev === "" ? String(campaignRes.data.concurrencyLimit ?? 5) : prev));
            }
            if (callsRes?.data) {
                setCalls(callsRes.data.calls || []);
                setTotalCalls(callsRes.data.total || 0);
            }
        } catch (err) {
            console.error("Failed to load campaign", err);
        } finally {
            setLoading(false);
        }
    }, [campaignId, page, statusFilter, durationFilter]);

    useEffect(() => {
        setLoading(true);
        load();
    }, [load]);

    // Clear row selection whenever the page or status filter changes
    useEffect(() => {
        setSelected(new Set());
    }, [page, statusFilter, durationFilter, campaignId]);

    // Lead statuses for the "remove + set status" dropdown
    useEffect(() => {
        (async () => {
            try {
                const res = await fetchLeadStatuses();
                setLeadStatuses((res?.data || []).map((s) => ({ label: s.label || s.value, value: s.value || s.label })));
            } catch (err) {
                // non-fatal
            }
        })();
    }, []);

    // Auto-refresh while running
    useEffect(() => {
        if (campaign?.status !== "running") return;
        const timer = setInterval(load, 10000);
        return () => clearInterval(timer);
    }, [campaign?.status, load]);

    const handleSaveSpeed = async () => {
        const n = Math.min(50, Math.max(1, parseInt(speedInput, 10) || 0));
        if (!n) { toast.error("Enter a number between 1 and 50"); return; }
        setSavingSpeed(true);
        try {
            const res = await updateCampaignSettings(campaignId, { concurrencyLimit: n });
            if (res?.data) {
                setCampaign(res.data);
                setSpeedInput(String(res.data.concurrencyLimit));
            }
            toast.success(`Call speed set to ${n} concurrent call${n > 1 ? "s" : ""}${campaign?.status === "running" ? " — takes effect within a few seconds" : ""}`);
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to update call speed");
        } finally {
            setSavingSpeed(false);
        }
    };

    const doAction = async (action, fn, confirmText) => {
        if (confirmText && !window.confirm(confirmText)) return;
        setActionLoading(true);
        try {
            await fn(campaignId);
            toast.success(`Campaign ${action}`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.message || `Failed to ${action} campaign`);
        } finally {
            setActionLoading(false);
        }
    };

    const toggleSelect = (callId) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(callId)) next.delete(callId);
            else next.add(callId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        setSelected((prev) => {
            if (prev.size === calls.length && calls.length > 0) return new Set();
            return new Set(calls.map((c) => c._id));
        });
    };

    const handleRemove = async () => {
        const callIds = Array.from(selected);
        if (callIds.length === 0) return;
        const statusMsg = removeStatus ? ` and set their status to "${removeStatus}"` : "";
        if (!window.confirm(`Remove ${callIds.length} lead(s) from this campaign${statusMsg}? The leads stay in the CRM.`)) return;
        setRemoving(true);
        try {
            const res = await removeCampaignCalls(campaignId, { callIds, newLeadStatus: removeStatus || undefined });
            toast.success(res?.message || "Leads removed");
            setSelected(new Set());
            setRemoveStatus("");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to remove leads");
        } finally {
            setRemoving(false);
        }
    };

    const handleRerun = async () => {
        if (!window.confirm("Re-run this campaign? All remaining leads will be queued and called again.")) return;
        setActionLoading(true);
        try {
            await rerunCampaign(campaignId);
            toast.success("Campaign re-run started");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to re-run campaign");
        } finally {
            setActionLoading(false);
        }
    };

    if (loading && !campaign) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
        );
    }

    if (!campaign) {
        return (
            <div className="text-center py-20">
                <p className="text-sm text-gray-500">Campaign not found</p>
                <Button variant="outline" size="sm" onClick={onBack} className="mt-3">
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </Button>
            </div>
        );
    }

    const totals = campaign.totals || {};
    const totalCount = (totals.queued || 0) + (totals.calling || 0) + (totals.completed || 0) + (totals.failed || 0);
    const doneCount = (totals.completed || 0) + (totals.failed || 0);
    const progress = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;
    const totalPages = Math.max(1, Math.ceil(totalCalls / limit));

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={onBack}>
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-lg font-semibold text-gray-900">{campaign.name}</h2>
                            <Badge className={`text-xs ${CAMPAIGN_STATUS_STYLES[campaign.status] || ""}`}>
                                {campaign.status}
                            </Badge>
                        </div>
                        {campaign.description && <p className="text-xs text-gray-500 mt-0.5">{campaign.description}</p>}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={load}>
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                    {(campaign.status === "draft" || campaign.status === "paused") && (
                        <Button
                            size="sm"
                            onClick={() => doAction("started", startCampaign)}
                            disabled={actionLoading}
                            className="bg-green-600 hover:bg-green-700 text-white"
                        >
                            <Play className="h-4 w-4 mr-1" /> Start
                        </Button>
                    )}
                    {campaign.status === "running" && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => doAction("paused", pauseCampaign)}
                            disabled={actionLoading}
                        >
                            <Pause className="h-4 w-4 mr-1" /> Pause
                        </Button>
                    )}
                    {["draft", "running", "paused"].includes(campaign.status) && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => doAction("cancelled", cancelCampaign, "Cancel this campaign? Queued calls will not be made.")}
                            disabled={actionLoading}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                            <XCircle className="h-4 w-4 mr-1" /> Cancel
                        </Button>
                    )}
                    {["completed", "cancelled"].includes(campaign.status) && (
                        <Button
                            size="sm"
                            onClick={handleRerun}
                            disabled={actionLoading}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                            <RotateCcw className="h-4 w-4 mr-1" /> Re-run
                        </Button>
                    )}
                </div>
            </div>

            {/* Totals cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {[
                    { label: "Queued", value: totals.queued || 0, icon: Clock, color: "text-gray-500" },
                    { label: "Calling", value: totals.calling || 0, icon: PhoneCall, color: "text-green-600" },
                    { label: "Completed", value: totals.completed || 0, icon: CheckCircle2, color: "text-blue-600" },
                    { label: "Failed", value: totals.failed || 0, icon: AlertTriangle, color: "text-red-600" }
                ].map((card) => (
                    <div key={card.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-gray-500">{card.label}</p>
                            <card.icon className={`h-4 w-4 ${card.color}`} />
                        </div>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
                    </div>
                ))}
            </div>

            {/* Progress bar */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-4">
                <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs font-medium text-gray-500">Progress</p>
                    <p className="text-xs text-gray-500">{doneCount} / {totalCount} ({progress}%)</p>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-600 rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
            </div>

            {/* Call speed (concurrency) — editable live */}
            {["draft", "running", "paused"].includes(campaign.status) && (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <Gauge className="h-4 w-4 text-indigo-600" />
                            <div>
                                <p className="text-sm font-medium text-gray-900">Call speed</p>
                                <p className="text-xs text-gray-500">
                                    Number of calls placed at the same time (1–50).
                                    {campaign.status === "running" && " Changes apply live within a few seconds — no need to pause."}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                min={1}
                                max={50}
                                value={speedInput}
                                onChange={(e) => setSpeedInput(e.target.value)}
                                className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <Button
                                size="sm"
                                onClick={handleSaveSpeed}
                                disabled={savingSpeed || String(campaign.concurrencyLimit) === String(parseInt(speedInput, 10))}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            >
                                {savingSpeed ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Calls table */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900">Calls ({totalCalls})</h3>
                    <div className="flex items-center gap-2">
                        <div className="w-40">
                            <Select
                                options={Object.entries(DURATION_RANGES).map(([value, r]) => ({ label: r.label, value }))}
                                value={durationFilter}
                                onChange={(v) => { setDurationFilter(v); setPage(1); }}
                                placeholder="Any duration"
                            />
                        </div>
                        <div className="w-40">
                            <Select
                                options={[
                                    { label: "All statuses", value: "" },
                                    { label: "Queued", value: "queued" },
                                    { label: "Calling", value: "calling" },
                                    { label: "Completed", value: "completed" },
                                    { label: "Failed", value: "failed" },
                                    { label: "Cancelled", value: "cancelled" }
                                ]}
                                value={statusFilter}
                                onChange={(v) => { setStatusFilter(v); setPage(1); }}
                                placeholder="All statuses"
                            />
                        </div>
                    </div>
                </div>

                {/* Bulk remove toolbar */}
                {canEdit && selected.size > 0 && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-2.5 bg-indigo-50/70 border-b border-indigo-100">
                        <span className="text-xs font-medium text-indigo-800">{selected.size} selected</span>
                        <div className="flex items-center gap-2 sm:ml-auto">
                            <div className="w-52">
                                <Select
                                    options={[{ label: "Keep lead status", value: "" }, ...leadStatuses]}
                                    value={removeStatus}
                                    onChange={setRemoveStatus}
                                    placeholder="Keep lead status"
                                />
                            </div>
                            <Button
                                size="sm"
                                onClick={handleRemove}
                                disabled={removing}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                {removing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                                Remove from campaign
                            </Button>
                        </div>
                    </div>
                )}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50/80">
                            <tr>
                                {canEdit && (
                                    <th className="px-4 py-2.5 text-left w-10">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                                            checked={calls.length > 0 && selected.size === calls.length}
                                            onChange={toggleSelectAll}
                                            title="Select all on this page"
                                        />
                                    </th>
                                )}
                                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Lead</th>
                                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Duration</th>
                                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Result</th>
                                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Lead Status</th>
                                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {calls.length === 0 ? (
                                <tr>
                                    <td colSpan={canEdit ? "8" : "7"} className="px-4 py-10 text-center text-sm text-gray-400">
                                        No calls{statusFilter ? " with this status" : ""}
                                    </td>
                                </tr>
                            ) : calls.map((call) => (
                                <tr key={call._id} className={`hover:bg-gray-50/50 ${selected.has(call._id) ? "bg-indigo-50/40" : ""}`}>
                                    {canEdit && (
                                        <td className="px-4 py-2.5">
                                            <input
                                                type="checkbox"
                                                className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                                                checked={selected.has(call._id)}
                                                onChange={() => toggleSelect(call._id)}
                                            />
                                        </td>
                                    )}
                                    <td className="px-4 py-2.5 text-sm text-gray-900">
                                        {call.lead ? `${call.lead.firstName || ""} ${call.lead.lastName || ""}`.trim() || "—" : "—"}
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-gray-600 font-mono">{call.toNumber}</td>
                                    <td className="px-4 py-2.5">
                                        <Badge className={`text-xs ${CALL_STATUS_STYLES[call.status] || ""}`}>{call.status}</Badge>
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-gray-600">{formatDuration(call.durationSeconds)}</td>
                                    <td className="px-4 py-2.5 text-xs text-gray-500 max-w-[200px] truncate">
                                        {call.error || call.disconnectionReason || (call.callSuccessful !== undefined && call.callSuccessful !== null ? (call.callSuccessful ? "successful" : "not successful") : "—")}
                                    </td>
                                    <td className="px-4 py-2.5 text-xs">
                                        {call.leadStatusAfter && call.leadStatusAfter !== call.leadStatusBefore ? (
                                            <span className="text-gray-600">{call.leadStatusBefore || "—"} → <span className="font-medium text-gray-900">{call.leadStatusAfter}</span></span>
                                        ) : (
                                            <span className="text-gray-400">{call.leadStatusBefore || "—"}</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        {(call.transcript || call.callSummary || call.recordingUrl) && (
                                            <Button variant="ghost" size="sm" onClick={() => setTranscriptCall(call)}>
                                                <FileText className="h-4 w-4 text-indigo-600" />
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500">Page {page} of {totalPages}</p>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Transcript modal */}
            <Dialog open={!!transcriptCall} onOpenChange={(v) => !v && setTranscriptCall(null)}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Call Details</DialogTitle>
                        <DialogDescription>
                            {transcriptCall?.lead
                                ? `${transcriptCall.lead.firstName || ""} ${transcriptCall.lead.lastName || ""}`.trim()
                                : ""} — {transcriptCall?.toNumber}
                        </DialogDescription>
                    </DialogHeader>
                    {transcriptCall?.recordingUrl && (
                        <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1">Recording</p>
                            <audio controls className="w-full" src={transcriptCall.recordingUrl} />
                        </div>
                    )}
                    {transcriptCall?.callSummary && (
                        <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1">Summary</p>
                            <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{transcriptCall.callSummary}</p>
                        </div>
                    )}
                    {transcriptCall?.transcript && (
                        <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1">Transcript</p>
                            <pre className="text-xs text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap font-sans max-h-80 overflow-y-auto">{transcriptCall.transcript}</pre>
                        </div>
                    )}
                    {transcriptCall && Object.keys(transcriptCall.customAnalysisData || {}).length > 0 && (
                        <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1">Analysis Data</p>
                            <pre className="text-xs text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap max-h-40 overflow-y-auto">{JSON.stringify(transcriptCall.customAnalysisData, null, 2)}</pre>
                        </div>
                    )}
                    {transcriptCall && campaign?.retellAgentId && (
                        <CallAnalysisPanel call={transcriptCall} agentId={campaign.retellAgentId} />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
